/**
 * Action: tag_person
 *
 * Apply a single tag to a single person. The inverse is
 * `untag_person` (registered alongside) so one-click undo works.
 *
 * This is intentionally the simplest possible concrete action — a
 * single-row insert into `taggings` with the engine's audit
 * wrapper. The point isn't the tag itself; it's to validate the
 * end-to-end pipe: proposal → preview → confirm → execute → audit
 * → undo. Subsequent actions (create_task, send_email) follow the
 * same shape.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const tagPersonParams = z.object({
  person_id: z.string().guid(),
  tag_id: z.string().guid(),
});

type TagPersonParams = z.infer<typeof tagPersonParams>;

const untagPersonParams = tagPersonParams;
type UntagPersonParams = z.infer<typeof untagPersonParams>;

/**
 * Shared resolver — turn an (org, person_id, tag_id) tuple into the
 * person/tag display names for previews + summaries. Keeping this
 * inline (not a separate helper module) until a second action needs
 * the same lookup.
 */
async function resolvePersonAndTag(
  organizationId: string,
  personId: string,
  tagId: string,
): Promise<{ personName: string; tagName: string }> {
  const [personResult, tagResult] = await Promise.all([
    supabase
      .from("people")
      .select("id, first_name, last_name")
      .eq("organization_id", organizationId)
      .eq("id", personId)
      .maybeSingle(),
    supabase
      .from("tags")
      .select("id, name")
      .eq("organization_id", organizationId)
      .eq("id", tagId)
      .maybeSingle(),
  ]);

  if (personResult.error || !personResult.data) {
    throw new Error("Person not found in this organization.");
  }
  if (tagResult.error || !tagResult.data) {
    throw new Error("Tag not found in this organization.");
  }

  const first = (personResult.data.first_name ?? "").trim();
  const last = (personResult.data.last_name ?? "").trim();
  const personName = [first, last].filter(Boolean).join(" ") || "this person";
  return { personName, tagName: tagResult.data.name };
}

// ---------------------------------------------------------------------------
// tag_person
// ---------------------------------------------------------------------------

const tagPersonDefinition: ActionDefinition<TagPersonParams> = {
  id: "tag_person",
  label: "Tag person",
  description: "Apply a single tag to a person.",
  requiredTier: "staff",
  paramsSchema: tagPersonParams,
  defaultRequiresConfirm: false,
  async buildPreview(params, ctx) {
    const { personName, tagName } = await resolvePersonAndTag(
      ctx.organizationId,
      params.person_id,
      params.tag_id,
    );
    return { summary: `Tag ${personName} with "${tagName}"` };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const { personName, tagName } = await resolvePersonAndTag(
      ctx.organizationId,
      params.person_id,
      params.tag_id,
    );

    // Idempotent: if the tagging already exists, treat the action
    // as a no-op success. The unique index on (organization_id,
    // tag_id, taggable_type, taggable_id) means we'd otherwise get
    // a 23505 error that's not a real failure from the user's
    // perspective.
    const { error } = await supabase.from("taggings").upsert(
      {
        organization_id: ctx.organizationId,
        tag_id: params.tag_id,
        taggable_type: "Person",
        taggable_id: params.person_id,
      },
      { onConflict: "organization_id,tag_id,taggable_type,taggable_id" },
    );

    if (error) {
      return {
        ok: false,
        summary: `Failed to tag ${personName}`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "tag.apply",
      tableName: "taggings",
      recordId: params.person_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        tag_id: params.tag_id,
        tag_name: tagName,
        person_id: params.person_id,
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Tagged ${personName} with "${tagName}"`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "untag_person",
        params: {
          person_id: params.person_id,
          tag_id: params.tag_id,
        },
        label: `Undo: remove "${tagName}"`,
      },
    };
  },
};

// ---------------------------------------------------------------------------
// untag_person (the inverse — also usable standalone)
// ---------------------------------------------------------------------------

const untagPersonDefinition: ActionDefinition<UntagPersonParams> = {
  id: "untag_person",
  label: "Remove tag from person",
  description: "Remove a single tag from a person.",
  requiredTier: "staff",
  paramsSchema: untagPersonParams,
  defaultRequiresConfirm: false,
  async buildPreview(params, ctx) {
    const { personName, tagName } = await resolvePersonAndTag(
      ctx.organizationId,
      params.person_id,
      params.tag_id,
    );
    return { summary: `Remove "${tagName}" from ${personName}` };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const { personName, tagName } = await resolvePersonAndTag(
      ctx.organizationId,
      params.person_id,
      params.tag_id,
    );

    const { error } = await supabase
      .from("taggings")
      .delete()
      .eq("organization_id", ctx.organizationId)
      .eq("tag_id", params.tag_id)
      .eq("taggable_type", "Person")
      .eq("taggable_id", params.person_id);

    if (error) {
      return {
        ok: false,
        summary: `Failed to remove tag from ${personName}`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "tag.remove",
      tableName: "taggings",
      recordId: params.person_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      oldData: {
        tag_id: params.tag_id,
        tag_name: tagName,
        person_id: params.person_id,
      },
      newData: {
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Removed "${tagName}" from ${personName}`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "tag_person",
        params: {
          person_id: params.person_id,
          tag_id: params.tag_id,
        },
        label: `Undo: re-apply "${tagName}"`,
      },
    };
  },
};

// Register on import. Consumers import this module via the actions
// barrel for the side effect.
registerAction(tagPersonDefinition);
registerAction(untagPersonDefinition);

export { tagPersonDefinition, untagPersonDefinition };
